import { useEffect } from "react";
import { useEvent } from "../context/EventProvider";
import { fallbackOf } from "./catalog";
import { useSettings, useT } from "./context";

/**
 * ═══ LE NOM DE L'ÉVÉNEMENT, TEL QUE LE SITE LE MONTRE ═══
 *
 * Trois sources, dans cet ordre :
 *
 *   1. le réglage `FO_MEETING_V2_EVENT_NAME`, quand l'organisateur en a écrit un ;
 *   2. le nom de la table `Events` (`event.name`), que le contexte porte déjà ;
 *   3. « Événement », le repli du catalogue — pour un événement sans nom du tout.
 *
 * ⚠️ LA DEUXIÈME MANQUAIT (bug #259). Le haut de page et le badge ne lisaient que le
 * réglage, et celui-ci n'était rempli que par la reprise V1 du panneau (retirée depuis) :
 * un événement jamais repris affichait « Événement » partout, alors que son nom était là,
 * dans le contexte.
 *
 * ⚠️ LE RÉGLAGE ÉCRIT GAGNE, TOUJOURS : l'événement 408 s'appelle « 1 jour » en base et
 * « bonjour2 » sur son site. Un réglage fait d'espaces n'est pas un nom.
 *
 * ⚠️ PURE ET PARTAGÉE, parce qu'elle a TROIS lecteurs sur le site : le haut de page, le
 * badge et l'onglet du navigateur. Deux copies de cette règle, c'est un onglet qui donne
 * au site un autre nom que sa propre page. Le panneau la lit aussi (bug #285) : l'exemple
 * du champ vide et la recherche montrent le nom que le site affiche, pas « Événement ».
 *
 * Rend `""` quand ni le réglage ni la table n'ont de nom : le repli, lui, se traduit,
 * et c'est au hook de le faire.
 */
export function eventNameOf(written: unknown, name: string | undefined): string {
  if (typeof written === "string" && written.trim() !== "") return written;
  return (name ?? "").trim();
}

export const EVENT_NAME_KEY = "FO_MEETING_V2_EVENT_NAME";

/** Le nom que le haut de page et le badge affichent — voir `eventNameOf`. */
export function useEventName(): string {
  const written = useSettings().value(EVENT_NAME_KEY);
  const event = useEvent();
  const say = useT();
  return (
    eventNameOf(written, event?.name) ||
    say(EVENT_NAME_KEY, fallbackOf(EVENT_NAME_KEY))
  );
}

/**
 * ═══ L'ONGLET PORTE LE NOM DE L'ÉVÉNEMENT ═══
 *
 * ⚠️ RIEN NE L'ÉCRIVAIT : l'onglet gardait le `<title>` d'`index.html` — « Inscription »
 * pour tous les événements, et sur tous les écrans, badge et live compris.
 *
 * ⚠️ PAS AVANT QUE L'ÉVÉNEMENT SOIT RÉSOLU. Le nom n'est pas encore connu, et poser le
 * repli « Événement » le temps d'une requête ferait clignoter l'onglet vers un mot qui
 * ne nomme rien. « Inscription » tient la place d'ici là.
 *
 * ⚠️ NI AVANT QUE SES RÉGLAGES SOIENT LÀ (bug #350) : l'événement connu, les lignes
 * restaient celles du SEED tant que le catalogue des langues était en route, et l'onglet
 * affichait le nom de démonstration, « Salon Tech 2026 ». Même garde que l'écran
 * d'attente du Shell : `ready`.
 */
export function useDocumentTitle(): void {
  const name = useEventName();
  const { ready } = useSettings();
  useEffect(() => {
    if (ready) document.title = name;
  }, [ready, name]);
}
